import { useMutation } from '@apollo/client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@grupoboticario/flora-react'

import { AccelerationFormState, CreateCampaignPayload } from '@/components/AccelerationForm/AccelerationForm.types'
import AccelerationStepper from '@/components/AccelerationForm/AccelerationStepper'
import {
  normalizeCampaignBrandFlags,
  resolveSharedContentButtonUrl,
  serializeContentByBrandForApi
} from '@/components/AccelerationForm/utils/brandForApi'
import { serializeAccelerationContentForApi } from '@/components/AccelerationForm/utils/contentForApi'
import { serializeAccelerationPointsForApi } from '@/components/AccelerationForm/utils/pointsForApi'
import { ModalPublishConfirmation } from '@/components/ModalPublishConfirmation'
import { ModalPublishedCampaign } from '@/components/ModalPublishedCampaign'
import { CAMPAIGN_TYPE } from '@/constants/accelerationTable'
import { useUserContext } from '@/context/user'
import CREATE_CAMPAIGN from '@/graphql/mutation/createCampaign'
import { setEndOfDay, setStartOfDay } from '@/utils/format/setHours'
import { businessUnit } from '@/utils/themes/theme'
import triggerToastError from '@/utils/triggerToastError/triggerToastError'
import triggerToastSuccess from '@/utils/triggerToastSuccess/triggerToastSuccess'

import * as P from '../pages.styles'

const NewAccelerationPage = () => {
  const [formData, setFormData] = useState<AccelerationFormState | null>(null)
  const [campaignIdVoucher, setCampaignIdVoucher] = useState('')
  const [openPublishConfirmationModal, setOpenPublishConfirmationModal] = useState(false)
  const [openPublishCampaignModal, setOpenPublishCampaignModal] = useState(false)
  const { user: loggedUser } = useUserContext()
  const navigate = useNavigate()

  const [createCampaignCall, { loading: createCampaignLoading }] = useMutation(CREATE_CAMPAIGN)

  const createCampaign = async () => {
    if (!formData) return

    const brand = normalizeCampaignBrandFlags(formData.brand)
    const contentByBrand = serializeContentByBrandForApi(brand, formData.contentByBrand ?? {})

    try {
      const campaign: CreateCampaignPayload = {
        name: formData.name,
        type: formData.type,
        segmentation: {
          campaignType: formData.segmentation.campaignType,
          audienceType: formData.segmentation.audienceType
        },
        businessUnit: businessUnit(globalThis.location.hostname),
        responsible: typeof loggedUser?.email === 'string' ? loggedUser.email : '',
        startDate: setStartOfDay(formData.startDate),
        endDate: setEndOfDay(formData.endDate),
        points: serializeAccelerationPointsForApi(formData.points),
        brand,
        contentByBrand,
        content: {
          ...serializeAccelerationContentForApi(formData.content),
          buttonUrl: resolveSharedContentButtonUrl(brand, formData.contentByBrand ?? {}, formData.content.buttonUrl)
        },
        ...(formData.type === CAMPAIGN_TYPE.MULTIPLIER
          ? { multiplier: Number(formData.multiplier) }
          : { point: Number(formData.point) })
      }

      const { data } = await createCampaignCall({
        variables: {
          campaign
        }
      })

      if (data?.createCampaign?.error?.message) {
        triggerToastError('Ops! Ocorreu um erro ao criar a campanha de acelerador. Tente novamente.')
      }

      if (data?.createCampaign?.campaign?.campaignId) {
        setCampaignIdVoucher(data?.createCampaign?.campaign?.campaignId)

        triggerToastSuccess(false, 'Campanha de acelerador criada com sucesso!', () => {
          setOpenPublishConfirmationModal(false)
          setFormData(null)
          setOpenPublishCampaignModal(true)
        })
      }
    } catch {
      triggerToastError('Ops! Ocorreu um erro ao criar a campanha de acelerador. Tente novamente.')
    }
  }

  const handlePublish = (values: AccelerationFormState) => {
    delete values.content?.imageUrl
    setFormData(values)
    setOpenPublishConfirmationModal(true)
  }

  return (
    <Box css={P.pageWrapperCss}>
      <AccelerationStepper onPublish={handlePublish} onCancel={() => navigate('/acceleration')} />

      <ModalPublishConfirmation
        handleSubmit={createCampaign}
        isLoading={createCampaignLoading}
        openPublishConfirmationModal={openPublishConfirmationModal}
        setOpenPublishConfirmationModal={setOpenPublishConfirmationModal}
      />
      <ModalPublishedCampaign
        campaignIdVoucher={campaignIdVoucher}
        openPublishCampaignModal={openPublishCampaignModal}
        setOpenPublishCampaignModal={setOpenPublishCampaignModal}
      />
    </Box>
  )
}

export { NewAccelerationPage }
